"use client"

import { useState, useCallback, useMemo, useEffect, useRef } from "react"
import { Toolbar } from "./toolbar"
import { FolderCard } from "./folder-card"
import { ProjectCard } from "./project-card"
import { AssignProjectsDialog } from "./assign-projects-dialog"
import { EmptyState } from "./empty-state"
import { OptionsControlModal } from "../options-control-modal"
import { getUserId } from "../../../API"
import type { Project, Folder } from "../../../lib/types"
import { FOLDER_COLORS, initialFolders } from "../../../lib/mock-data"

interface FolderManagerProps {
  project2: Project2[]
}

type Project2 = {
  id: number
  title: string
  thumbnail: string
  description: string
  category: string
  rating?: number
  createdAt?: string
  tags?: string[]
  author?: string
  status?: "active" | "draft" | "archived"
  sport_id: string
  svg_path: string
  id_data: number
  shared: string
  body_state: boolean
  socks_state: boolean
  up_state: boolean
  down_state: boolean
  clone_state: boolean
  images: any
  inside_color: string
  up_state2: boolean
  collar: string
}

/**
 * API global para scripts externos:
 * window.folderAPI.addFolder({ id, name, parentId?, projectIds? })
 * window.folderAPI.addSubfolder({ id, parentId, name, projectIds? })
 * window.folderAPI.getFolders()
 * window.folderAPI.navigate(folderId|null)
 */
declare global {
  interface Window {
    folderAPI?: {
      addFolder: (params: {
        id: number | string
        name: string
        parentId?: string | null
        projectIds?: number[]
      }) => Folder | null

      addSubfolder: (params: {
        id: number | string
        parentId: string
        name: string
        projectIds?: number[]
      }) => Folder | null

      getFolders: () => Folder[]
      navigate: (folderId: string | null) => void

      // ✅ novo: atualiza uma pasta existente (ex.: projectIds) sem recarregar
      updateFolder?: (folderId: string, patch: Partial<Folder>) => boolean

      // ✅ novo:
      getCurrentFolder: () => { id: string | null; name: string | null }

      // ✅ novo (sem supabase): adiciona um projeto na pasta atual (estado local)
      addProjectToCurrentFolder: (projectId: number) => {
        ok: boolean
        folderId: string | null
        projectIds?: number[]
        reason?: string
      }
    }
  }
}

export function FolderManager({ project2 }: FolderManagerProps) {
  const [folders, setFolders] = useState<Folder[]>(initialFolders)
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [assignFolderId, setAssignFolderId] = useState<string | null>(null)
  const [shareFolderId, setShareFolderId] = useState<string | null>(null)
  const [isShareOpen, setIsShareOpen] = useState(false)

  // Refs "vivos" para evitar closure stale nas chamadas externas
  const foldersRef = useRef<Folder[]>(folders)
  const currentFolderIdRef = useRef<string | null>(currentFolderId)

  useEffect(() => {
    foldersRef.current = folders
  }, [folders])

  useEffect(() => {
    currentFolderIdRef.current = currentFolderId
  }, [currentFolderId])

  // Build breadcrumbs by walking up the parentId chain
  const breadcrumbs = useMemo(() => {
    const crumbs: { id: string | null; name: string }[] = [{ id: null, name: "Pastas" }]
    if (!currentFolderId) return crumbs

    const chain: Folder[] = []
    let fId: string | null = currentFolderId

    while (fId) {
      const folder = folders.find((f) => f.id === fId)
      if (!folder) break
      chain.unshift(folder)
      fId = folder.parentId ?? null
    }

    for (const folder of chain) {
      crumbs.push({ id: folder.id, name: folder.name })
    }

    return crumbs
  }, [currentFolderId, folders])

  // Cria pasta no "nível atual" (dentro do currentFolderId)
  const createFolder = useCallback((name: string, id: number, projectIds: number[]) => {
    const trimmed = (name ?? "").trim()
    if (!trimmed) return null

    const newFolder: Folder = {
      id: id.toString(),
      name: trimmed,
      color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)],
      createdAt: new Date().toISOString().split("T")[0],
      projectIds: projectIds ?? [],
      parentId: currentFolderIdRef.current,
    }

    setFolders((prev) => [...prev, newFolder])
    return newFolder
  }, [])

  const createSubfolder = useCallback(
    (parentId: string, name: string, projectIds: number[], id: number) => {
      const trimmed = (name ?? "").trim()
      if (!trimmed) return null

      const newFolder: Folder = {
        id: id.toString(),
        name: trimmed,
        color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)],
        createdAt: new Date().toISOString().split("T")[0],
        projectIds: projectIds ?? [],
        parentId,
      }

      setFolders((prev) => [...prev, newFolder])
      return newFolder
    },
    []
  )

  const renameFolder = useCallback((folderId: string, newName: string) => {
    setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, name: newName } : f)))
  }, [])

  const deleteFolder = useCallback(
    (folderId: string) => {
      // Also delete all descendant folders
      setFolders((prev) => {
        const idsToDelete = new Set<string>()
        const collectIds = (id: string) => {
          idsToDelete.add(id)
          prev.filter((f) => f.parentId === id).forEach((f) => collectIds(f.id))
        }
        collectIds(folderId)
        return prev.filter((f) => !idsToDelete.has(f.id))
      })

      // If we're inside the deleted folder, navigate back
      if (currentFolderId === folderId) {
        const deleted = foldersRef.current.find((f) => f.id === folderId)
        setCurrentFolderId(deleted?.parentId ?? null)
      }
    },
    [currentFolderId]
  )

  const assignProjectsToFolder = useCallback((folderId: string, projectIds: number[]) => {
    setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, projectIds } : f)))
  }, [])

  const navigate = useCallback((folderId: string | null) => {
    setCurrentFolderId(folderId)
  }, [])

  // ✅ EXPOE NO WINDOW para scripts externos
  useEffect(() => {
    if (typeof window === "undefined") return

    window.folderAPI = {
      addFolder: ({ id, name, parentId = null, projectIds = [] }) => {
        const trimmed = (name ?? "").trim()

        if (!trimmed) return null

        const folderId = String(id)

        // evita duplicar id
        if (foldersRef.current.some((f) => f.id === folderId)) return null

        // se parentId foi informado, valida se existe
        if (parentId !== null && parentId !== undefined) {
          if (!foldersRef.current.some((f) => f.id === parentId)) return null
        }

        const newFolder: Folder = {
          id: folderId,
          name: trimmed,
          color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)],
          createdAt: new Date().toISOString().split("T")[0],
          projectIds,
          parentId: parentId ?? null, // null = raiz
        }

        setFolders((prev) => [...prev, newFolder])
        return newFolder
      },

      addSubfolder: ({ id, parentId, name, projectIds = [] }) => {
        const trimmed = (name ?? "").trim()

        if (!trimmed) return null

        const folderId = String(id)

        if (foldersRef.current.some((f) => f.id === folderId)) return null

        //if (!foldersRef.current.some((f) => f.id === parentId)) return null

        const parentIdNew = String(parentId)

        const newFolder: Folder = {
          id: folderId,
          name: trimmed,
          color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)],
          createdAt: new Date().toISOString().split("T")[0],
          projectIds,
          parentId: parentIdNew,
        }

        setFolders((prev) => [...prev, newFolder])
        return newFolder
      },

      getFolders: () => foldersRef.current,

      // Atualiza uma pasta existente em memória (ex.: projectIds) sem recarregar a página
      updateFolder: (folderId: string, patch: Partial<Folder>) => {
        setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, ...patch } : f)))
        return true
      },

      navigate: (folderId: string | null) => {
        setCurrentFolderId(folderId)
      },

      getCurrentFolder: () => {
        const id = currentFolderIdRef.current

        if (!id) {
          return { id: null, name: "Pastas" } // raiz
        }

        const folder = foldersRef.current.find(f => f.id === id)

        return {
          id,
          name: folder?.name ?? null
        }
      },

      addProjectToCurrentFolder: (projectId: number) => {
        const folderId = currentFolderIdRef.current
        if (!folderId) {
          return { ok: false, folderId: null, reason: "no-folder-open" }
        }

        const folder = foldersRef.current.find((f) => f.id === folderId)
        if (!folder) {
          return { ok: false, folderId, reason: "folder-not-found" }
        }

        const current = Array.isArray(folder.projectIds) ? folder.projectIds : []
        const next = current.includes(projectId) ? current : [...current, projectId]

        // atualiza o estado (UI reflete na hora)
        setFolders((prev) => prev.map((f) => (f.id === folderId ? { ...f, projectIds: next } : f)))

        // opcional: emitir evento pra scripts externos reagirem
        window.dispatchEvent(
          new CustomEvent("folder:project-added", { detail: { folderId, projectId, projectIds: next } })
        )

        return { ok: true, folderId, projectIds: next }
      },
    }

    return () => {
      delete window.folderAPI
    }
  }, [])

  // Current view data
  const currentFolder = folders.find((f) => f.id === currentFolderId) ?? null
  const assignFolder = folders.find((f) => f.id === assignFolderId) ?? null

  // Show child folders of the current level
  const visibleFolders = folders.filter((f) => f.parentId === currentFolderId)

  // Projects in the current folder
  const folderProjects = currentFolder
    ? project2.filter((p) => currentFolder.projectIds.includes(p.id))
    : []

  // Helper to count direct subfolders of a folder
  const getSubfolderCount = (folderId: string) =>
    folders.filter((f) => f.parentId === folderId).length

  const hasContent = currentFolderId
    ? visibleFolders.length > 0 || folderProjects.length > 0
    : visibleFolders.length > 0

  // Abre o modal de compartilhamento apontando para a pasta (mesmo mecanismo das categorias)
  const handleShareFolder = (folderId: string) => {
    const uid = getUserId() || localStorage.getItem("user_id")
    ;(window as any).categoryShared = uid + "&s=" + folderId
    ;(window as any).projectSharedID = folderId
    setShareFolderId(folderId)
    setIsShareOpen(true)
  }

  return (
    <div className="flex min-h-screen flex-col bg-background w-full">
      <Toolbar breadcrumbs={breadcrumbs} onCreateFolder={createFolder} onNavigate={navigate} />

      <main className="flex-1 px-6 py-6">
        {!currentFolderId ? (
          /* Root level: only folders */
          visibleFolders.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {visibleFolders.map((folder) => (
                <FolderCard
                  key={folder.id}
                  folder={folder}
                  projectCount={folder.projectIds.length}
                  subfolderCount={getSubfolderCount(folder.id)}
                  onOpen={navigate}
                  onRename={renameFolder}
                  onDelete={deleteFolder}
                  onAssignProjects={setAssignFolderId}
                  onCreateSubfolder={createSubfolder}
                  onShare={handleShareFolder}
                />
              ))}
            </div>
          ) : (
            <EmptyState type="folders" />
          )
        ) : (
          /* Inside a folder: subfolders + projects */
          <>
            {visibleFolders.length > 0 && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {visibleFolders.map((folder) => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    projectCount={folder.projectIds.length}
                    subfolderCount={getSubfolderCount(folder.id)}
                    onOpen={navigate}
                    onRename={renameFolder}
                    onDelete={deleteFolder}
                    onAssignProjects={setAssignFolderId}
                    onCreateSubfolder={createSubfolder}
                    onShare={handleShareFolder}
                  />
                ))}
              </div>
            )}

            {folderProjects.length > 0 && (
              <div className={visibleFolders.length > 0 ? "mt-6" : ""}>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                  {folderProjects.map((project) => (
                    <ProjectCard key={project.id} project2={project} />
                  ))}
                </div>
              </div>
            )}

            {!hasContent && <EmptyState type="projects" />}
          </>
        )}
      </main>

      <AssignProjectsDialog
        open={!!assignFolderId}
        onOpenChange={(open) => {
          if (!open) setAssignFolderId(null)
        }}
        folder={assignFolder}
        projects={project2}
        onAssign={assignProjectsToFolder}
      />

      <OptionsControlModal
        open={isShareOpen}
        onOpenChange={setIsShareOpen}
        onSave={() => {}}
        colorOptions={[]}
      />
    </div>
  )
}